import {Component, Input, OnInit} from '@angular/core';
import {FormControl, FormControlStatus, FormGroup} from '@angular/forms';
import {NavigationHelperService} from '@application/helper/navigation-helper/navigation-helper.service';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {PlanningEquipment} from '@domain/planning-prototype/planning-equipment';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {AssertionUtils, BaseComponent, DateTimeFormat, DateTimePipe} from '@vdw/angular-component-library';
import {DateTime} from 'luxon';
import {distinctUntilKeyChanged, filter, map, startWith, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';
import {NavigationPLanningItemDateSelect} from './navigation-planning-item-date-select.interface';

@Component({
  selector: 'app-planning-item-date-select',
  templateUrl: './planning-item-date-select.component.html',
  styleUrls: ['./planning-item-date-select.component.scss']
})
export class PlanningItemDateSelectComponent extends BaseComponent implements OnInit {
  @Input()
  public earliestStartDateControl: FormControl<DateTime>;

  @Input()
  public minimumDurationControl: FormControl<number>;

  public planningItem: PlanningItem;
  public isCompleted = false;
  public planningItemForm = new FormGroup({
    earliestStartDate: new FormControl<DateTime>(null),
    earliestStartTime: new FormControl('')
  });

  public startDate: Date;
  public endDate: Date;

  public constructor(
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly navigationHelperService: NavigationHelperService<NavigationPLanningItemDateSelect>,
    private readonly dateTimePipe: DateTimePipe
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setPlanningItemOnSelectedItemChanged();
    if (this.earliestStartDateControl) {
      this.syncValueChanges();
      this.syncStatusChanges();
    }
  }

  public onNavigationHelperDestroy(): void {
    this.navigationHelperService.savePartialState<NavigationPLanningItemDateSelect>({
      planningItemDateSelect: this.planningItem,
      isCompletedDateSelect: this.isCompleted,
      startDateDateSelect: this.startDate,
      endDateDateSelect: this.endDate
    });
  }

  private syncValueChanges(): void {
    this.earliestStartDateControl.valueChanges
      .pipe(
        startWith(undefined),
        map(() => this.earliestStartDateControl.value),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((startDate: DateTime) => {
        const split = this.splitDateTime(startDate);
        this.planningItemForm.controls.earliestStartDate.setValue(split.date);
        this.planningItemForm.controls.earliestStartTime.setValue(split.time);
      });

    this.planningItemForm.valueChanges
      .pipe(
        map(() => this.planningItemForm.value),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe(({earliestStartDate, earliestStartTime}: {earliestStartDate: DateTime; earliestStartTime: string}) => {
        if (AssertionUtils.isNullOrUndefined(earliestStartDate)) {
          this.planningItemForm.controls.earliestStartTime.disable({emitEvent: false});
        } else if (this.planningItemForm.controls.earliestStartDate.enabled) {
          this.planningItemForm.controls.earliestStartTime.enable({emitEvent: false});
        }
        const dateTime = this.joinDateTime(earliestStartDate, earliestStartTime);
        if (dateTime !== this.earliestStartDateControl.value) {
          this.earliestStartDateControl.setValue(dateTime, {emitEvent: false});
          this.earliestStartDateControl.markAsDirty();
        }
      });
  }

  private syncStatusChanges(): void {
    this.earliestStartDateControl.statusChanges
      .pipe(
        startWith(undefined),
        map(() => this.earliestStartDateControl.status),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe((formState: FormControlStatus) => {
        if (formState === 'DISABLED') {
          this.planningItemForm.controls.earliestStartDate.disable();
          this.planningItemForm.controls.earliestStartTime.disable();
        } else {
          this.planningItemForm.controls.earliestStartDate.enable();
          this.planningItemForm.controls.earliestStartTime.enable();
        }
      });
  }

  private splitDateTime(dateTime: DateTime): {date: DateTime; time: string} {
    if (AssertionUtils.isNullOrUndefined(dateTime)) {
      return {
        date: null,
        time: '00:00'
      };
    }

    return {
      date: dateTime.startOf('day'),
      time: this.dateTimePipe.transform(dateTime, DateTimeFormat.TIME_SIMPLE)
    };
  }

  private joinDateTime(date: DateTime, time: string = '00:00'): DateTime {
    if (AssertionUtils.isNullOrUndefined(date) || AssertionUtils.isNullOrUndefined(time)) {
      return null;
    }
    const dateTime = DateTime.fromFormat(time, time.includes('M') ? 'h:mm a' : 'H:mm');

    if (!dateTime.isValid) {
      return null;
    }
    date = date.set({hour: dateTime.hour, minute: dateTime.minute});

    return date;
  }

  private setPlanningItemOnSelectedItemChanged(): void {
    this.planningContext.selectedItemChanges
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((item: PlanningEquipment | PlanningItem) => item instanceof PlanningItem),
        distinctUntilKeyChanged('id')
      )
      .subscribe((item: PlanningItem) => {
        this.planningItem = item;
        this.isCompleted = !AssertionUtils.isNullOrUndefined(item.actualEndDate);
        const forecast = this.planningContext.sortedPlanningItemForecasts[item.draftId];
        this.startDate = PlanningItemHelper.getTimelineStartDate(item, forecast);
        this.endDate = PlanningItemHelper.getTimelineEndDate(item, forecast);
      });

    let obj = {
      planningItemDateSelect: null,
      isCompletedDateSelect: null,
      startDateDateSelect: null,
      endDateDateSelect: null
    };
    let prevState = this.navigationHelperService.getPartialState<NavigationPLanningItemDateSelect>(Object.keys(obj));
    if (!AssertionUtils.isNullOrUndefined(prevState)) {
      this.planningItem = prevState.planningItemDateSelect;
      this.isCompleted = prevState.isCompletedDateSelect;
      this.startDate = prevState.startDateDateSelect;
      this.endDate = prevState.endDateDateSelect;
    }
  }
}
